import {SorterResult} from 'antd/es/table/interface';
import {useCallback, useMemo, useState} from 'react';

type TableSorterStateType<ColumnIndex extends string> = {
    columnKey: ColumnIndex;
    sortOrder: 'ascend' | 'descend' | null;
};

export type TableSorterHookReturnType<ColumnIndex extends string = string> = {
    getSortOrder: (columnKey: ColumnIndex) => 'ascend' | 'descend' | undefined | null;
    setSorterOrder: <RecordType>(sorter: SorterResult<RecordType> | Array<SorterResult<RecordType>>) => void;
    sorterState: TableSorterStateType<ColumnIndex> | null;
    sorterQueryValue: string | null;
};

/* eslint-disable no-undefined */
export function useTableSorter<ColumnIndex extends string>(options?: {
    getSorterQueryValue: (columnKey: ColumnIndex, sortOrder: 'ascend' | 'descend' | null) => string;
}): TableSorterHookReturnType<ColumnIndex> {
    const [sorterState, setSorterState] = useState<null | TableSorterStateType<ColumnIndex>>(null);

    const getSortOrder = useCallback(
        (columnKey: string): 'ascend' | 'descend' | undefined | null => {
            if (!sorterState) {
                return undefined;
            }

            return sorterState.columnKey === columnKey ? sorterState.sortOrder : undefined;
        },
        [sorterState]
    );

    const setSorterOrder = useCallback(
        <RecordType,>(sorter: SorterResult<RecordType> | Array<SorterResult<RecordType>>) => {
            if (!Array.isArray(sorter)) {
                setSorterState({
                    columnKey: sorter.column?.dataIndex as ColumnIndex,
                    sortOrder: sorter.order ?? null,
                });
            }
        },
        []
    );

    const sorterQueryValue = useMemo(() => {
        if (!sorterState || !sorterState.columnKey) {
            return null;
        }

        if (options?.getSorterQueryValue) {
            return options?.getSorterQueryValue(sorterState.columnKey, sorterState.sortOrder);
        }

        return `${sorterState.sortOrder === 'descend' ? '-' : ''}${sorterState.columnKey}`;
    }, [options, sorterState]);

    return {
        getSortOrder,
        setSorterOrder,
        sorterState,
        sorterQueryValue,
    };
}
